import {
    Component,
    HostBinding,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Optional,
    Self,
    SimpleChanges
} from '@angular/core';
import {BasicEntityInterface} from "../../../basic-entity-back/basic-entity-interface/basic-entity-interface";
import {Resource} from "../../../api/resource";
import {InterfaceProviderService} from "../../../basic-entity-back/services/interface-provider.service";
import {ControlValueAccessor, UntypedFormControl, NgControl} from "@angular/forms";
import {MatFormFieldControl} from "@angular/material/form-field";
import {BehaviorSubject, Subject, Subscription} from "rxjs";
import {Uri} from "../../../api/uri";
import {coerceBooleanProperty} from "@angular/cdk/coercion";
import {Material} from "../../../model/materiales/material.model";
import {FilterAndData} from "../../../api/filter-list";
import {ExactSearchFilter} from "../../../basic-entity-back/filters/search-filter";
import {map} from "rxjs/operators";
import {int} from "three/examples/jsm/nodes/shadernode/ShaderNodeBaseElements";

@Component({
    selector: 'be-cached-input',
    templateUrl: './cached-input.component.html',
    styleUrls: ['./cached-input.component.scss'],
    providers: [
        {provide: MatFormFieldControl, useExisting: CachedInputComponent}
    ]
})
export class CachedInputComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor, MatFormFieldControl<Uri | Resource> {
    public static nextId = 0;
    public stateChanges = new Subject<void>();
    @HostBinding() public id = `be-key-value-input-${CachedInputComponent.nextId++}`;

    @Input() public filters = [];
    /** Las opciones filtradas se cargan sin sacar el «Cargando» global, que se come los clics. */
    @Input() public cargaSilenciosa = false;
    /** Carga previa a las opciones (la del padre): el desplegable avisa de que aún está cargando. */
    @Input() public cargando = false;
    private _esperandoFiltro = false;
    private _pendientes = 0;

    /** Hay opciones por llegar: lo que avisa el padre, el filtro nuevo en espera o una carga filtrada en curso. */
    public get cargandoOpciones(): boolean {
        return this.cargando || this._esperandoFiltro || this._pendientes > 0;
    }
    @Input() public selectedValue = null;
    @Input() public autoSelectFirst = true;

    @Input()
    get placeholder(): string {
        return this._placeholder;
    }

    set placeholder(value: string) {
        this._placeholder = value;
        this.stateChanges.next();
    }

    private _placeholder: string;

    public get focused() {
        return this._focused;
    }

    private _focused = false;

    public get empty() {
        return !this.control.value;
    }

    @HostBinding('class.floating')
    public get shouldLabelFloat() {
        return this.focused || !this.empty;
    }

    @Input()
    public get required() {
        return this._required;
    }

    public set required(req) {
        this._required = coerceBooleanProperty(req);
        this.stateChanges.next();
    }

    private _required = false;

    @Input()
    get disabled() {
        return this.control.disabled;
    }

    set disabled(dis) {
        const disabled = coerceBooleanProperty(dis);
        this.setDisabledState(disabled);
    }

    public get errorState(): boolean {
        return !!this.ngControl.errors;
    }

    public readonly controlType = 'be-cached-input';
    @HostBinding('attr.aria-describedby') public describedBy = '';

    @Input()
    public get value(): Uri | null {
        return this.control.value;
    }

    public set value(uri: Uri | null) {
        this.writeValue(uri);
    }

    setDescribedByIds(ids: string[]): void {
        this.describedBy = ids.join(' ');
    }

    onContainerClick(event: MouseEvent): void {
    }

    /**
     * Qué opción del mat-select corresponde al valor del control. Compara por IRI, no por
     * identidad: el valor puede llegar de fuera (un `setValue()` del formulario, la línea que se
     * está editando) y no ser la misma instancia que la opción cargada de la API.
     *
     * Antes era `p1.iri === p2.iri`. Con `onlyUri` -el caso de la dirección de entrega- tanto el
     * valor del control como el `[value]` de cada opción son ya el IRI, así que los dos lados daban
     * `undefined` y la comparación era cierta para CUALQUIER pareja: el desplegable pintaba la
     * primera opción de la lista, no la seleccionada. Reproducido en navegador en cnc-client, que
     * es donde la plantilla sí engancha esta función (2026-09-23).
     *
     * **Aquí, en cnc-admin, la plantilla NO usa `[compareWith]`**, así que hoy esto no se llama
     * desde el desplegable: `mat-select` compara por identidad y acierta porque `option()` resuelve
     * el valor a la instancia de la propia opción. Se corrige igualmente para que las dos copias
     * del componente digan lo mismo y para que enganchar `[compareWith]` -o escribir el valor
     * desde fuera con las opciones ya cargadas- no reviva el fallo.
     */
    compareFn(p1, p2): boolean {
        const iri1 = p1 instanceof Uri ? p1 : p1?.iri;
        const iri2 = p2 instanceof Uri ? p2 : p2?.iri;

        return !!iri1 && !!iri2 && iri1.isTheSame(iri2);
    }

    /** Interface of the entities referenced by the URIs which are selectable here */
    @Input() public modelTypeOrParent: Function;
    @Input() public onlyUri = false;

    public interfs: BasicEntityInterface<Resource>[];
    public options = new Map<BasicEntityInterface<Resource>, Resource[]>();
    public control = new UntypedFormControl('');

    private _onChange = _ => {
    };
    private _onTouched = () => {
    };
    private _subscription: Subscription;
    /**
     * Carga en curso de las opciones. Cada `loadData()` cancela la anterior: si el desplegable
     * nace sin filtros (se suscribe a `manager.all`) y el filtro llega después, las páginas que
     * sigan entrando en la caché no deben pisar la lista filtrada; y la respuesta tardía de un
     * filtro anterior tampoco debe pisar la del filtro nuevo.
     */
    private _carga: Subscription;

    constructor(public interfaceProvider: InterfaceProviderService,
                @Optional() @Self() public ngControl: NgControl) {
        if (this.ngControl != null) {
            this.ngControl.valueAccessor = this;
        }
    }

    ngOnInit(): void {
        this.interfs = this.interfaceProvider.allAcceptedInterfaces(this.modelTypeOrParent);
        if (this.filters?.length === 0) {
            this.loadData();
        }
        this._subscription = this.control.valueChanges.subscribe(this._manageChange.bind(this));
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.filters && changes.filters.currentValue && changes.filters.currentValue.length > 0) {
            this._esperandoFiltro = true;
            setTimeout(() => this.loadData(changes.filters.currentValue), 200);
        }
        if (changes.selectedValue) {
            this.selectedValue = changes.selectedValue.currentValue;
        }
    }


    public loadData(filters?: FilterAndData[]) {
        this.interfs = this.interfaceProvider.allAcceptedInterfaces(this.modelTypeOrParent);
        this._carga?.unsubscribe();
        this._carga = new Subscription();
        this._esperandoFiltro = false;
        this._pendientes = 0;
        const carga = this._carga;
        for (const interf of this.interfs) {
            const sinProp = filters === undefined || filters.filter(f => !f.data).length > 0;
            if (filters && !sinProp) {
                let objs = []
                this._pendientes++;
                const terminada = () => {
                    if (carga === this._carga) {
                        this._pendientes--;
                    }
                };
                this._carga.add(this.interfaceProvider.managerForModel(interf.serialiser.model).loader.findAndFollow(filters, undefined, undefined, undefined, undefined, undefined, this.cargaSilenciosa).pipe(
                    map(r => r.member)
                ).subscribe(objsT => {
                    objs = [...objs, ...objsT];
                    this.options.set(interf, objs);
                    if (this.control.value) {
                        this.control.setValue(this.option(this.control.value), {emitEvent: false});
                        if (this.selectedValue) {
                            this.writeValue(this.selectedValue, true)
                        }
                    } else {
                        if (this.selectedValue) {
                            this.writeValue(this.selectedValue, true)
                        }
                    }

                    if (objs.length === 1 && this.autoSelectFirst) {
                        this.writeValue(objs[0], true);
                    }
                }, terminada, terminada));
            } else {
                this._carga.add(this.interfaceProvider.managerForModel(interf.serialiser.model)
                    .all.subscribe(objs => {
                    this.options.set(interf, objs);
                    if (this.control.value) {
                        this.control.setValue(this.option(this.control.value), {emitEvent: false});
                        if (this.selectedValue) {
                            this.writeValue(this.selectedValue, true)
                        }
                    } else {
                        if (this.selectedValue) {
                            this.writeValue(this.selectedValue, true)
                        }
                    }
                    if (objs.length === 1 && this.autoSelectFirst) {
                        setTimeout(() => this.writeValue(objs[0], true), 100);
                    }
                }));
            }
        }
    }

    /**
     * When the value of the control changes
     */
    private _manageChange(v: Resource | Uri | null) {
        this._onChange(v);
    }

    public optionValue(opt: Resource | null): Resource | Uri | null {
        if (!opt) {
            return opt;
        }
        return this.onlyUri ? opt.iri : opt;
    }

    ngOnDestroy(): void {
        this._subscription && this._subscription.unsubscribe();
        this._carga?.unsubscribe();
    }

    registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.control.disable();
        } else {
            this.control.enable();
        }
    }

    public option(copy: Resource | Uri): Resource | Uri {
        const iri = copy instanceof Resource ? copy.iri : copy;
        const found = Array.from(this.options.values())
            .reduce((p, c) => c.concat(p), [])
            .find(v => v.iri.isTheSame(iri));
        return found ? (this.onlyUri ? found.iri || iri : found) : copy;
    }

    writeValue(obj: any, emit = false): void {
        this.control.setValue(obj != null ? this.option(obj) : null, {emitEvent: emit});
    }
}
